import React, { useContext, useState } from "react";

import { Autorenew, BlockOutlined } from "@mui/icons-material";
import { Chip, Tooltip } from "@mui/material";
import { useParams } from "react-router-dom";

import { User } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { UserUrlParamTypes } from "../../types/user-display";
import ConfirmDialog from "../shared/confirm-dialog";
import { getUserLoginStatus, unblockUserLogin } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";

enum Auth0Status {
  NONE = "none",
  ERROR = "error",
  BLOCKED = "blocked",
  UNBLOCKED = "unblocked",
}

interface Auth0LoginButtonProps {
  user: User | undefined;
}

const Auth0LoginStatusButton: React.FC<Auth0LoginButtonProps> = ({
  user,
}: Auth0LoginButtonProps) => {
  const { regionCode, userId } = useParams<UserUrlParamTypes>();
  const [auth0Status, setAuth0Status] = useState(Auth0Status.NONE);
  const [inprogress, setInprogress] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const email = user?.email || "";
  const authId = user?.authId || "";

  const { showError } = useContext(SnackbarContext);

  const getStatus = () => {
    setInprogress(true);
    getUserLoginStatus({ authId }, callOptionsHeaders(regionCode, userId))
      .then(({ isBlocked }) => {
        setAuth0Status(isBlocked ? Auth0Status.BLOCKED : Auth0Status.UNBLOCKED);
      })
      .catch((error) => {
        setAuth0Status(Auth0Status.ERROR);
        showError(error);
      })
      .finally(() => {
        setInprogress(false);
      });
  };

  const unblockUser = async () => {
    setInprogress(true);
    unblockUserLogin({ authId }, callOptionsHeaders(regionCode, userId))
      .catch((error) => {
        setAuth0Status(Auth0Status.ERROR);
        showError(error);
      })
      .finally(() => {
        setInprogress(false);
        getStatus();
      });
  };

  if (!inprogress && auth0Status === Auth0Status.NONE && authId !== "") {
    // on first load get the status from Auth0
    getStatus();
  }

  const shouldSpin = inprogress ? "spin-1s" : "";
  let auth0Btn;
  switch (auth0Status) {
    case Auth0Status.BLOCKED:
      auth0Btn = (
        <Tooltip title="Click to unlock">
          <Chip
            variant="outlined"
            disabled={inprogress}
            label="Locked"
            color="secondary"
            size="small"
            onClick={() => setOpenConfirmDialog(true)}
            icon={
              inprogress ? (
                <Autorenew className={shouldSpin} />
              ) : (
                <BlockOutlined />
              )
            }
          />
        </Tooltip>
      );
      break;
    case Auth0Status.UNBLOCKED:
      auth0Btn = (
        <Tooltip title="Click to refresh">
          <Chip
            variant="outlined"
            disabled={inprogress}
            label="Unlocked"
            color="default"
            style={{ color: "#178a02" }}
            size="small"
            icon={
              <Autorenew className={shouldSpin} style={{ color: "#178a02" }} />
            }
            onClick={getStatus}
          />
        </Tooltip>
      );
      break;
    case Auth0Status.NONE:
    case Auth0Status.ERROR:
    default:
      auth0Btn = (
        <Tooltip title="Click to refresh">
          <Chip
            variant="outlined"
            disabled={inprogress}
            label="Check Status"
            color="default"
            size="small"
            icon={<Autorenew className={shouldSpin} />}
            onClick={getStatus}
          />
        </Tooltip>
      );
      break;
  }

  return (
    <>
      {auth0Btn}
      <ConfirmDialog
        title="Unlock account"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={unblockUser}
        cancelBtnLabel="No"
        confirmBtnLabel="Yes"
      >
        Are you sure you want to unlock the account for&nbsp;<b>{email}</b>?
      </ConfirmDialog>
    </>
  );
};

export default Auth0LoginStatusButton;
